import { KaboodleToaster } from "components/common";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useUploadWithProgress } from "hooks/useUploadWithProgress";
import { useState } from "react";
import {
  useCreateDrawerPresignedUploadUrlMutation,
  useDeleteDocumentMutation,
  useFinalizeDrawerUploadMutation,
  useUploadDocumentMutation,
  useUploadDocumentForClaimMutation,
  useUploadDocumentForRREMutation,
} from "services/documents-api";
import type { RootState } from "services/store";
import { extractErrorMessage } from "utils/error-utils";
import { useSelector } from "utils/redux";

import type { DocumentType, ServiceType } from "../DocumentTypes";

type MutationConfig = {
  fileId?: number;
  claimId?: number;
  rreId?: number;
  serviceId?: number;
  serviceType?: ServiceType;
  additionalParams?: Record<string, string>;
};

/**
 * reusable document mutation handlers (upload, delete)
 * can be used standalone or composed into larger hooks
 * handles file-based, claim-based, and RRE-based document uploads
 *
 * when the documents.enable_presigned_drawer_upload feature flag is on,
 * uploads route through a presigned S3 URL + finalize step (mirroring the
 * intake portal flow); otherwise they use the legacy multipart endpoint.
 */
export const useDocumentMutations = (config: MutationConfig) => {
  const { fileId, claimId, rreId, serviceId, serviceType, additionalParams } =
    config;

  const [uploadDocument, uploadMutation] = useUploadDocumentMutation();
  const [uploadDocumentForClaim, uploadClaimMutation] =
    useUploadDocumentForClaimMutation();
  const [uploadDocumentForRRE, uploadRREMutation] =
    useUploadDocumentForRREMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [createDrawerPresignedUploadUrl] =
    useCreateDrawerPresignedUploadUrlMutation();
  const [finalizeDrawerUpload] = useFinalizeDrawerUploadMutation();
  const { startUpload } = useUploadWithProgress();
  const [presignedActiveCount, setPresignedActiveCount] = useState(0);

  const constants = useSelector((state: RootState) => state.shared.constants);
  const isPresignedDrawerUploadEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.PRESIGNED_DRAWER_UPLOAD_ENABLED],
  );

  const arScreeningDocumentTypeForServiceType = (
    explicit?: string,
  ): string | undefined =>
    explicit || (serviceType === "ars" ? "NEW_DISCOVERY" : undefined);

  const buildServiceIdFields = (): Record<string, string> => {
    if (serviceId === undefined || !serviceType) return {};
    const id = serviceId.toString();
    switch (serviceType) {
      case "msa":
        return { msa_id: id };
      case "ars":
        return { amended_review_screening_id: id };
      case "lcp":
      case "mcp":
        return { cost_projection_id: id };
      case "lien":
        return { lien_file_id: id };
      case "docs":
        return { document_management_file_id: id };
      case "CON":
      case "AUD":
        return { miscellaneous_service_id: id };
      default:
        return {};
    }
  };

  // Only reached via handleUpload, which already rejects empty files
  const handlePresignedUpload = async (
    files: File[],
    documentType?: string,
    arScreeningDocumentType?: string,
    isCmsSubmissionDocument?: boolean,
    serviceFieldOverrides?: Record<string, string>,
    suppressToast?: boolean,
  ): Promise<boolean> => {
    setPresignedActiveCount((c) => c + files.length);

    const resolvedArScreeningType = arScreeningDocumentTypeForServiceType(
      arScreeningDocumentType,
    );

    const finalizeBaseFields = {
      ...(fileId ? { file_id: fileId.toString() } : {}),
      ...(claimId ? { claim_id: claimId.toString() } : {}),
      ...(rreId ? { rre_id: rreId.toString() } : {}),
      ...(documentType ? { type: documentType } : {}),
      ...(resolvedArScreeningType
        ? { ar_screening_document_type: resolvedArScreeningType }
        : {}),
      ...(isCmsSubmissionDocument !== undefined
        ? { is_cms_submission_document: isCmsSubmissionDocument }
        : {}),
      ...buildServiceIdFields(),
      ...(additionalParams || {}),
      ...(serviceFieldOverrides || {}),
    };

    try {
      // Uploads in parallel; the drawer calls this one file at a time, but
      // Claims/RRE still pass a whole batch, so keep it batch-capable
      const results = await Promise.allSettled(
        files.map(async (file) => {
          const presigned = await createDrawerPresignedUploadUrl({
            filename: file.name,
            content_type: file.type || "application/octet-stream",
          }).unwrap();

          await new Promise<void>((resolve, reject) => {
            startUpload({
              file,
              presignedPostUrl: presigned.upload_url,
              presignedPostFields: presigned.fields,
              onComplete: () => resolve(),
              onError: (err) => reject(err),
            });
          });

          await finalizeDrawerUpload({
            ...finalizeBaseFields,
            s3_key: presigned.s3_key,
            original_filename: file.name,
          }).unwrap();
        }),
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failureCount = results.length - successCount;

      if (successCount > 0 && failureCount === 0) {
        if (!suppressToast)
          KaboodleToaster.success("Document uploaded successfully");
        return true;
      }
      if (successCount > 0 && failureCount > 0) {
        // A mixed batch result; only possible for a non-gate caller like
        // Claims/RRE, since the gate always calls this one file at a time
        const message = `Uploaded ${successCount} of ${results.length} files. ${failureCount} failed.`;
        if (suppressToast) throw new Error(message);
        KaboodleToaster.error(message);
        return false;
      }

      const firstFailure = results.find(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const message = extractErrorMessage(
        firstFailure?.reason,
        "Failed to upload document",
      );
      if (suppressToast) throw new Error(message);
      KaboodleToaster.error(message);
      return false;
    } finally {
      setPresignedActiveCount((c) => Math.max(0, c - files.length));
    }
  };

  const handleUpload = async (
    files: File[],
    documentType?: string,
    arScreeningDocumentType?: string,
    isCmsSubmissionDocument?: boolean,
    serviceFieldOverrides?: Record<string, string>,
    suppressToast?: boolean,
  ) => {
    if (files.length === 0) {
      if (suppressToast) throw new Error("No files provided");
      return false;
    }

    if (isPresignedDrawerUploadEnabled) {
      return handlePresignedUpload(
        files,
        documentType,
        arScreeningDocumentType,
        isCmsSubmissionDocument,
        serviceFieldOverrides,
        suppressToast,
      );
    }

    try {
      if (claimId) {
        await uploadDocumentForClaim({
          files,
          claimId: claimId.toString(),
          documentType,
        }).unwrap();
      } else if (rreId) {
        await uploadDocumentForRRE({
          files,
          rreId: rreId.toString(),
          documentType,
        }).unwrap();
      } else if (fileId) {
        await uploadDocument({
          files,
          fileId: fileId.toString(),
          serviceId: serviceId?.toString(),
          serviceType,
          documentType,
          arScreeningDocumentType: arScreeningDocumentTypeForServiceType(
            arScreeningDocumentType,
          ),
          isCmsSubmissionDocument,
          // Only merge into an object when one side is actually set, so callers
          // that pass neither keep seeing additionalParams === undefined
          additionalParams:
            additionalParams || serviceFieldOverrides
              ? {
                  ...(additionalParams || {}),
                  ...(serviceFieldOverrides || {}),
                }
              : undefined,
        }).unwrap();
      } else {
        // Nothing to upload to; don't fall through and report success
        throw new Error("No file, claim, or RRE association to upload to");
      }

      if (!suppressToast)
        KaboodleToaster.success("Document uploaded successfully");
      return true;
    } catch (error) {
      console.error("Failed to upload document:", error);
      const message = extractErrorMessage(error, "Failed to upload document");
      if (suppressToast) throw new Error(message);
      KaboodleToaster.error(message);
      return false;
    }
  };

  const handleDelete = async (document: DocumentType) => {
    try {
      await deleteDocument({
        documentId: document.id,
      }).unwrap();
      KaboodleToaster.success("Document deleted successfully");
      return true;
    } catch (error) {
      console.error("Failed to delete document:", error);
      KaboodleToaster.error("Failed to delete document");
      return false;
    }
  };

  return {
    handleUpload,
    handleDelete,
    isUploading:
      uploadMutation.isLoading ||
      uploadClaimMutation.isLoading ||
      uploadRREMutation.isLoading ||
      presignedActiveCount > 0,
  };
};
